import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  IconButton,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ViewModule as ViewModuleIcon,
  CloudQueue as CloudQueueIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Error as ErrorIcon,
  Timer as TimerIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ReactECharts from 'echarts-for-react';
import { ROUTE_PATHS } from '../constants/routePaths';
import { useModels } from '../hooks/useModels';
import { useMetrics } from '../hooks/useMetrics';
import { deploymentInstanceService } from '../services/DeploymentInstanceService';

export function DashboardPage() {
  const navigate = useNavigate();
  const { models, loading: modelsLoading } = useModels();
  const [deployments, setDeployments] = useState<any[]>([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<string>('24h');
  
  // Use metrics hook
  const { requestMetrics, tokenMetrics, summary, loading: metricsLoading, refreshMetrics } = useMetrics(timeRange);

  useEffect(() => {
    const fetchDeployments = async () => {
      try {
        const data = await deploymentInstanceService.getDeploymentInstances();
        setDeployments(data || []);
      } catch (error) {
        console.error('Failed to load deployments:', error);
      } finally {
        setDeploymentsLoading(false);
      }
    };

    fetchDeployments();
  }, []);

  // Format x-axis labels based on time range
  const formatXAxisLabel = (isoString: string): string => {
    const date = new Date(isoString);
    
    switch (timeRange) {
      case '1h':
        // Show minutes for 1 hour view
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      case '24h':
        // Show hours for 24 hour view
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      case '7d':
        // Show day and time for 7 days
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
      case '30d':
        // Show date only for 30 days
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      default:
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
  };

  // Chart configuration for Requests Over Time
  const requestChartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: ['Total Requests', 'Failed Requests'],
      top: 0
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: requestMetrics.map(m => formatXAxisLabel(m.time)),
      axisLabel: {
        rotate: timeRange === '30d' ? 45 : 0
      }
    },
    yAxis: {
      type: 'value',
      name: 'Requests'
    },
    series: [
      {
        name: 'Total Requests',
        type: 'line',
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(37, 99, 235, 0.6)' },
              { offset: 1, color: 'rgba(37, 99, 235, 0.1)' }
            ]
          }
        },
        lineStyle: {
          color: '#2563eb',
          width: 2
        },
        itemStyle: {
          color: '#2563eb'
        },
        data: requestMetrics.map(m => m.total)
      },
      {
        name: 'Failed Requests',
        type: 'line',
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(239, 68, 68, 0.6)' },
              { offset: 1, color: 'rgba(239, 68, 68, 0.1)' }
            ]
          }
        },
        lineStyle: {
          color: '#ef4444',
          width: 2
        },
        itemStyle: {
          color: '#ef4444'
        },
        data: requestMetrics.map(m => m.errors)
      }
    ]
  };

  // Error Rate Chart
  const errorRateChartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      formatter: (params: any) => {
        const param = params[0];
        return `${param.axisValue}<br/>${param.marker}${param.seriesName}: ${param.value.toFixed(2)}%`;
      }
    },
    legend: {
      data: ['Error Rate'],
      top: 0
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: requestMetrics.map(m => formatXAxisLabel(m.time)),
      axisLabel: {
        rotate: timeRange === '30d' ? 45 : 0
      }
    },
    yAxis: {
      type: 'value',
      name: 'Error Rate (%)',
      axisLabel: {
        formatter: '{value}%'
      }
    },
    series: [
      {
        name: 'Error Rate',
        type: 'line',
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(239, 68, 68, 0.6)' },
              { offset: 1, color: 'rgba(239, 68, 68, 0.1)' }
            ]
          }
        },
        lineStyle: {
          color: '#ef4444',
          width: 2
        },
        itemStyle: {
          color: '#ef4444'
        },
        data: requestMetrics.map(m => m.errorRate || 0)
      }
    ]
  };

  // Token Usage Chart
  const tokenChartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: ['Input Tokens', 'Output Tokens'],
      top: 0
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: tokenMetrics.map(m => formatXAxisLabel(m.time)),
      axisLabel: {
        rotate: timeRange === '30d' ? 45 : 0
      }
    },
    yAxis: {
      type: 'value',
      name: 'Tokens'
    },
    series: [
      {
        name: 'Input Tokens',
        type: 'line',
        smooth: true,
        lineStyle: {
          color: '#3b82f6',
          width: 2
        },
        itemStyle: {
          color: '#3b82f6'
        },
        data: tokenMetrics.map(m => m.inputTokens)
      },
      {
        name: 'Output Tokens',
        type: 'line',
        smooth: true,
        lineStyle: {
          color: '#10b981',
          width: 2
        },
        itemStyle: {
          color: '#10b981'
        },
        data: tokenMetrics.map(m => m.outputTokens)
      }
    ]
  };

  const stats = [
    {
      title: 'Total Requests',
      value: summary?.totalRequests || 0,
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      color: '#2563eb',
      bgColor: '#eff6ff',
      description: `${timeRange === '1h' ? 'Last hour' : timeRange === '24h' ? 'Last 24 hours' : timeRange === '7d' ? 'Last 7 days' : 'Last 30 days'}`,
      onClick: undefined
    },
    {
      title: 'Avg Latency',
      value: summary?.avgLatency 
        ? summary.avgLatency >= 1000 
          ? `${(summary.avgLatency / 1000).toFixed(2)}s` 
          : `${summary.avgLatency.toFixed(0)}ms`
        : '0ms',
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      color: '#f59e0b',
      bgColor: '#fef3c7',
      description: 'Response time',
      onClick: undefined
    },
    {
      title: 'Success Rate',
      value: `${summary?.successRate?.toFixed(2) || 0}%`,
      icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
      color: '#10b981',
      bgColor: '#d1fae5',
      description: 'Successful requests',
      onClick: undefined
    },
    {
      title: 'Error Rate',
      value: `${summary?.errorRate?.toFixed(2) || 0}%`,
      icon: <ErrorIcon sx={{ fontSize: 40 }} />,
      color: summary && summary.errorRate > 5 ? '#ef4444' : '#10b981',
      bgColor: summary && summary.errorRate > 5 ? '#fee2e2' : '#d1fae5',
      description: `${summary?.totalErrors || 0} failed`,
      onClick: undefined
    }
  ];

  const isLoading = modelsLoading || deploymentsLoading || metricsLoading;

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header with Time Range Selector */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#0f172a',
                mb: 1
              }}
            >
              Dashboard
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748b'
              }}
            >
              Overview of your AI platform metrics and resources
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="1h">Last Hour</MenuItem>
                <MenuItem value="24h">Last 24 Hours</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={refreshMetrics} size="small" color="primary">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Stats Grid */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {stats.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={stat.onClick}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          bgcolor: stat.bgColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: stat.color
                        }}
                      >
                        {stat.icon}
                      </Box>
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: '#0f172a',
                        mb: 0.5
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        mb: 0.5
                      }}
                    >
                      {stat.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#94a3b8'
                      }}
                    >
                      {stat.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Monitoring & Metrics Section */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#0f172a',
              mb: 3
            }}
          >
            Monitoring & Performance
          </Typography>

          <Grid container spacing={3}>
            {/* Requests Over Time Chart */}
            <Grid size={{ xs: 12, lg: 8 }}>
              <Paper sx={{ p: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: '#0f172a',
                    mb: 2
                  }}
                >
                  Requests Over Time
                </Typography>
                {metricsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <ReactECharts
                    option={requestChartOptions}
                    style={{ height: '350px' }}
                    notMerge={true}
                    lazyUpdate={true}
                  />
                )}
              </Paper>
            </Grid>

            {/* Error Rate Chart */}
            <Grid size={{ xs: 12, lg: 4 }}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: '#0f172a',
                    mb: 2
                  }}
                >
                  Error Rate Trend
                </Typography>
                {metricsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <ReactECharts
                    option={errorRateChartOptions}
                    style={{ height: '350px' }}
                    notMerge={true}
                    lazyUpdate={true}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Token Usage Chart - Full Width */}
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: '#0f172a',
                  mb: 2
                }}
              >
                Token Usage Over Time
              </Typography>
              {metricsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <ReactECharts
                  option={tokenChartOptions}
                  style={{ height: '350px' }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              )}
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
